import { useEffect, useRef, useState } from "react";
import { drawCover, hexToRgb } from "./canvasUtils";

/**
 * useGreenScreen — runtime chromakey + virtual-background compositor
 * for a single camera MediaStream.
 *
 * Rewritten from scratch (iter-180) to replace `useFxCompositor`. The
 * contract is deliberately simpler than the old hook:
 *
 *   • `stream`       — the MediaStream to composite FROM. Passed in
 *                      directly (NOT through a getter), so the effect
 *                      re-runs on stream identity changes without any
 *                      racy ref-plumbing.
 *   • `enabled`      — single on/off boolean. When false the hook
 *                      produces nothing; when true it builds the whole
 *                      pipeline (hidden <video> + <canvas> + rAF loop
 *                      + optional background <img>/<video>).
 *   • `background`   — `{ mode: "none"|"blur"|"image"|"video", dataUrl?,
 *                      url? }`. Mutated freely — the rAF tick reads it
 *                      via a ref each frame, so background swaps are
 *                      hot (no compositor rebuild).
 *   • `chroma`       — `{ color, similarity, smoothness }`. Also ref-read.
 *
 * Returns `{ track, bgLoading }`:
 *   • `track`     — the produced MediaStreamTrack (or null when
 *                    inactive). Consumers call `replaceTrack(track)`
 *                    on every outbound sender + swap it onto the local
 *                    preview's srcObject.
 *   • `bgLoading` — true while the current background image / video is
 *                   still downloading. UI surfaces this as a "Background
 *                   loading…" chip so hosts know the tile they just
 *                   picked is on the way, not broken.
 *
 * Design notes:
 *   • The produced track is owned by a hidden canvas — we re-capture it
 *     on every activation (teardown stops the previous one first).
 *   • Hot background swap watches `background.mode + (dataUrl||url)` as
 *     a stable key; when the key flips, we load the new asset and
 *     bump `bgLoading` until the resource reports ready.
 *   • Tracks are content-hinted "motion" so Chromium doesn't drop fps
 *     on canvas-captured streams (a legacy quality bug).
 */
export default function useGreenScreen({
  stream,
  enabled,
  background,
  chroma,
  smartFrame = false,
  // Manual framing override — "auto" (Smart Frame heuristic runs),
  // "tight" (force zoom-in to shoulders-up), or "wide" (no zoom, show
  // whole scene). When Smart Frame is off, `auto` is a no-op.
  frameMode = "auto",
  // Optional beauty/touch-up filter — applied to the person cutout
  // before compositing. When true, drop a light Gaussian blur +
  // brightness/contrast bump on the subject. Same trick Zoom / TikTok
  // Live use for their "Touch Up My Appearance" toggle. Off by default
  // so the raw camera detail is preserved unless the creator opts in.
  touchUp = false,
  // Auto Low-Light Boost — samples frame luminance (~1×/s) and lifts
  // brightness/contrast on the output when the room reads dark. EMA'd
  // so exposure never "pumps". Default ON — only engages when needed.
  lowLight = true,
  // Edge FX — soft drop shadow + rim light derived from the mask
  // silhouette. Grounds the cutout in virtual backgrounds so the host
  // doesn't look "pasted on". Off by default.
  edgeFx = false,
  // captureStream frame rate — 30 default, 60 for the 60fps quality
  // preset on capable phones.
  captureFps = 30,
  // Higher default resolution — 1920×1080 gives noticeably crisper
  // edges + more detail on the composite than the old 1280×720.
  // Modern phones handle 1080p at 30fps without breaking a sweat.
  width = 1920,
  height = 1080,
}) {
  const [track, setTrack] = useState(null);
  const [bgLoading, setBgLoading] = useState(false);
  // `ready` flips to true the first time we produce a segmentation mask —
  // used by the UI to show a "GS · Ready" indicator so the host knows
  // when the pipeline is warm (vs. still booting MediaPipe on first
  // enable). Kills the "why isn't it working" support ticket.
  const [ready, setReady] = useState(false);
  // `segStatus` — "idle" (before enabled), "loading", "ready", or
  // "failed" (MediaPipe couldn't load — e.g. blocked CDN, unsupported
  // browser). UIs can show a fallback message and offer "use blur"
  // when this is "failed".
  const [segStatus, setSegStatus] = useState("idle");
  // `bgFailed` — the last requested background failed to load (CORS,
  // 404, or decode error). UI can surface this so the host knows why
  // they're seeing a dark placeholder instead of their picked scene.
  const [bgFailed, setBgFailed] = useState(false);

  // Externally-visible refs — expose the segmentation output canvas
  // and the raw camera <video> element so chip live-previews can
  // share the single running segmentation loop instead of running
  // their own instance per chip (which would be N× more expensive).
  // Both mirror the current effect run and become { current: null }
  // when the hook is inactive.
  const subjectCanvasRef = useRef(null);
  const videoElRef = useRef(null);

  // Snapshot the latest settings in a ref so the rAF tick reads fresh
  // values every frame without re-keying the effect.
  const settingsRef = useRef({ background, chroma, smartFrame, frameMode, touchUp, lowLight, edgeFx });
  useEffect(() => { settingsRef.current = { background, chroma, smartFrame, frameMode, touchUp, lowLight, edgeFx }; }, [background, chroma, smartFrame, frameMode, touchUp, lowLight, edgeFx]);

  useEffect(() => {
    // ---- Inactive / no source → nothing to do ----
    const videoTrack = enabled ? stream?.getVideoTracks?.()[0] : null;
    const dbg = typeof window !== "undefined" && window.__TAPIN_FX_DEBUG;
    if (dbg) {
      console.log("[useGreenScreen] effect", {
        enabled,
        hasStream: !!stream,
        videoTracks: stream?.getVideoTracks?.().length || 0,
        videoTrackReady: videoTrack?.readyState,
      });
    }
    if (!enabled || !videoTrack) {
      setTrack(null);
      setBgLoading(false);
      setReady(false);
      if (dbg) console.log("[useGreenScreen] bailing — inactive or no source");
      return undefined;
    }

    // ---- Build the hidden camera <video> that feeds the canvas ----
    // NOTE: Firefox + some Chromes only produce drawable frames when
    // the element is attached to the document, so we slot it into a
    // 1×1 transparent off-screen container.
    // `disposed` — captured by every async closure (segmenter load,
    // rAF tick, image load handlers) so we can bail early when the
    // effect has torn down. Prevents leaked MediaPipe instances +
    // "setState on unmounted component" warnings.
    let disposed = false;
    const hiddenWrap = document.createElement("div");
    hiddenWrap.style.cssText =
      "position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none;";
    hiddenWrap.setAttribute("aria-hidden", "true");
    document.body.appendChild(hiddenWrap);

    const vid = document.createElement("video");
    vid.muted = true;
    vid.playsInline = true;
    vid.autoplay = true;
    vid.srcObject = new MediaStream([videoTrack]);
    hiddenWrap.appendChild(vid);
    vid.play().catch(() => { /* autoplay policy — will start on user gesture */ });
    videoElRef.current = vid;

    // ---- Canvas ----
    // iter212: two WebKit (iOS Safari) hardening changes here.
    //  1. The canvas is ATTACHED to the DOM (inside hiddenWrap) —
    //     captureStream() from a detached canvas is a known WebKit bug
    //     (frames never render into a <video>).
    //  2. captureStream() is NOT called here — resizing a canvas AFTER
    //     capture kills the stream on WebKit. We wait for the camera's
    //     real dimensions first (see `boot()` below), size every canvas
    //     once, and only THEN capture + publish the track.
    let curWidth = width;
    let curHeight = height;
    const canvas = document.createElement("canvas");
    canvas.width = curWidth;
    canvas.height = curHeight;
    canvas.style.cssText = "width:2px;height:2px;";
    hiddenWrap.appendChild(canvas);
    // NOTE: no `willReadFrequently` here — nothing ever reads pixels
    // back from the program canvas, and that flag forces the canvas
    // into CPU/software rendering (visibly softer scaling + slow at
    // 1080p on phones). GPU-accelerated 2d keeps the composite crisp.
    const ctx = canvas.getContext("2d");
    // High-quality scaling — single biggest visual lever for backgrounds.
    ctx.imageSmoothingEnabled = true;
    try { ctx.imageSmoothingQuality = "high"; } catch {}
    // Prime with a black frame so the first captured frame isn't garbage.
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, curWidth, curHeight);

    let produced = null;
    let producedTrack = null;

    // ---- MediaPipe Selfie Segmentation (Person Cutout · AI mode) -----
    // Lazy-loaded from a CDN the first time `chroma.mode === "person"`
    // is requested. Stores the raw soft mask on an offscreen canvas;
    // the render loop applies it as alpha via
    // `globalCompositeOperation = "destination-in"` with a Gaussian
    // blur on the mask — Zoom-style smooth edges, NO per-pixel JS.
    let engine = null;
    let engineLoading = false;
    let segBusy = false;
    let segBusyAt = 0;
    let haveMask = false;
    const segMaskCanvas = document.createElement("canvas");
    segMaskCanvas.width = curWidth; segMaskCanvas.height = curHeight;
    const segMaskCtx = segMaskCanvas.getContext("2d");
    segMaskCtx.imageSmoothingEnabled = true;
    try { segMaskCtx.imageSmoothingQuality = "high"; } catch {}
    const subjectCanvas = document.createElement("canvas");
    subjectCanvas.width = curWidth; subjectCanvas.height = curHeight;
    const subjectCtx = subjectCanvas.getContext("2d");
    subjectCanvasRef.current = subjectCanvas;
    // resizeAllCanvases — mid-stream dimension change (e.g. device
    // rotation). Resizing a captured canvas KILLS the stream on WebKit,
    // so after resizing we recreate the capture and republish the new
    // track — every consumer already handles track identity changes.
    const resizeAllCanvases = (w, h) => {
      if (w === curWidth && h === curHeight) return;
      curWidth = w;
      curHeight = h;
      canvas.width = w; canvas.height = h;
      segMaskCanvas.width = w; segMaskCanvas.height = h;
      subjectCanvas.width = w; subjectCanvas.height = h;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.filter = "none";
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 1.0;
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, w, h);
      if (produced) {
        try { produced.getTracks().forEach((t) => t.stop()); } catch {}
        try {
          produced = canvas.captureStream(captureFps);
          producedTrack = produced.getVideoTracks()[0];
          try { if (producedTrack) producedTrack.contentHint = "detail"; } catch {}
          setTrack(producedTrack);
        } catch { /* keep previous track */ }
      }
    };
    // Smart Frame plumbing — a tiny 80×80 canvas we draw the mask into,
    // then read pixels from to find the subject's bounding box. Sampling
    // at low res keeps the per-frame cost near zero. The bbox drives a
    // smoothly-EMA'd translate+scale we apply when compositing the
    // subject onto the program canvas, giving a steady-cam framing.
    const SF_SAMPLE = 80;
    const sfCanvas = document.createElement("canvas");
    sfCanvas.width = SF_SAMPLE; sfCanvas.height = SF_SAMPLE;
    const sfCtx = sfCanvas.getContext("2d", { willReadFrequently: true });
    // EMA target — current (smoothed) framing. Updated by the rAF when
    // smartFrame is enabled. 0 < EMA_ALPHA < 1; lower = smoother / lazier.
    const SF_EMA_ALPHA = 0.08;
    let sfTargetTx = 0;   // px translation in program-canvas coords
    let sfTargetTy = 0;
    let sfTargetScale = 1;
    // Frame counter so we don't recompute bbox every single frame.
    let sfFrameSkip = 0;
    // Auto Low-Light Boost plumbing — 32×32 luma sampler + EMA'd boost.
    const llCanvas = document.createElement("canvas");
    llCanvas.width = 32; llCanvas.height = 32;
    const llCtx = llCanvas.getContext("2d", { willReadFrequently: true });
    let llBoost = 1;
    let llFrame = 0;
    // ensureSegmenter — lazy-boots the segmentation engine on first
    // person-mode frame. iter214: upgraded to the modern
    // @mediapipe/tasks-vision MULTICLASS selfie model (hair-level edge
    // detail, GPU delegate) via ./segmenterEngine; falls back to the
    // legacy CDN selfie_segmentation when tasks-vision can't load.
    const ensureSegmenter = async () => {
      if (engine) return engine;
      if (engineLoading) return null;
      engineLoading = true;
      try {
        setSegStatus("loading");
        const { createSegmenterEngine } = await import("./segmenterEngine");
        const eng = await createSegmenterEngine({
          onMask: (maskSource) => {
            if (disposed) return;
            segMaskCtx.clearRect(0, 0, curWidth, curHeight);
            segMaskCtx.drawImage(maskSource, 0, 0, curWidth, curHeight);
            haveMask = true;
            setReady(true);
            setSegStatus("ready");
            segBusy = false;
          },
        });
        if (disposed) {
          try { eng?.close?.(); } catch { /* fine */ }
          return null;
        }
        engine = eng;
        return engine;
      } catch (err) {
        console.warn("[useGreenScreen] segmentation engine load failed:", err);
        if (!disposed) setSegStatus("failed");
        return null;
      } finally {
        engineLoading = false;
      }
    };

    // ---- Background image + video elements (swappable) ----
    // Background image element. NB: `crossOrigin` is set per-load
    // depending on the src (see setBgFromSettings). Setting it here
    // unconditionally breaks iOS Safari for data: URLs — it fails to
    // decode with a CORS error even though data: has no origin.
    const bgImg = new Image();

    const bgVid = document.createElement("video");
    bgVid.muted = true;
    bgVid.loop = true;
    bgVid.playsInline = true;
    bgVid.autoplay = true;
    hiddenWrap.appendChild(bgVid);

    // Background swap book-keeping — we dedupe by a stable key so the
    // tick loop doesn't re-assign `src` every frame.
    let bgKey = "";
    const setBgFromSettings = () => {
      const s = settingsRef.current.background || {};
      const src = s.dataUrl || s.url || "";
      const key = `${s.mode || "none"}|${src}`;
      if (key === bgKey) return;
      bgKey = key;
      setBgFailed(false);
      if (s.mode === "image" && src) {
        setBgLoading(true);
        bgImg.onload = () => { setBgLoading(false); setBgFailed(false); };
        bgImg.onerror = () => { setBgLoading(false); setBgFailed(true); };
        // Only require CORS for http(s) URLs; data:/blob: URLs already
        // have no cross-origin problem and setting crossOrigin=anonymous
        // on them makes iOS Safari fail the decode outright.
        if (/^https?:/i.test(src)) bgImg.crossOrigin = "anonymous";
        else bgImg.removeAttribute("crossorigin");
        bgImg.src = src;
      } else if (s.mode === "video" && src) {
        setBgLoading(true);
        bgVid.oncanplay = () => { setBgLoading(false); setBgFailed(false); };
        bgVid.onerror = () => { setBgLoading(false); setBgFailed(true); };
        if (/^https?:/i.test(src)) bgVid.crossOrigin = "anonymous";
        else bgVid.removeAttribute("crossorigin");
        bgVid.src = src;
        bgVid.play().catch(() => {});
      } else {
        setBgLoading(false);
      }
    };
    // Initial load — must happen BEFORE the first tick so the first
    // frame drawn doesn't flash black when the host picks a bg before
    // enabling chroma.
    setBgFromSettings();

    // drawSubjectLayer — paints optional Edge FX (soft drop shadow +
    // rim light derived from the mask silhouette) then the subject
    // cutout. Uses GPU ctx.filter blur — NOT ctx.shadowBlur, which is
    // CPU-rendered and tanks fps on big canvases.
    const drawSubjectLayer = (efx) => {
      if (efx && haveMask) {
        const shadowPx = Math.max(8, Math.round(curHeight * 0.012));
        ctx.save();
        ctx.filter = `blur(${shadowPx}px) brightness(0)`;
        ctx.globalAlpha = 0.4;
        ctx.drawImage(segMaskCanvas, 0, Math.round(curHeight * 0.014));
        ctx.restore();
        const rimPx = Math.max(6, Math.round(curHeight * 0.008));
        ctx.save();
        ctx.filter = `blur(${rimPx}px)`;
        ctx.globalAlpha = 0.38;
        ctx.drawImage(segMaskCanvas, 0, 0);
        ctx.restore();
      }
      ctx.drawImage(subjectCanvas, 0, 0);
    };

    // ---- rAF render loop ----
    let rafId = 0;
    const tick = () => {
      const { background: bg, chroma: ck, smartFrame: sf, frameMode: fm, touchUp: tu, lowLight: ll, edgeFx: efx } = settingsRef.current;
      setBgFromSettings(); // picks up hot swaps

      if (vid.readyState >= 2) {
        // Auto Low-Light Boost — sample luminance ~once/sec, lift
        // brightness/contrast when the room reads dark. EMA keeps the
        // transition smooth (no exposure "pumping").
        if (ll) {
          if ((llFrame++ % 30) === 0) {
            try {
              llCtx.drawImage(vid, 0, 0, 32, 32);
              const d = llCtx.getImageData(0, 0, 32, 32).data;
              let sum = 0;
              for (let i = 0; i < d.length; i += 4) sum += d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
              const luma = sum / 1024;
              const target = luma < 95 ? Math.min(1.55, 1 + ((95 - luma) / 95) * 0.8) : 1;
              llBoost += (target - llBoost) * 0.15;
            } catch { /* sampler is best-effort */ }
          }
        } else {
          llBoost = 1;
        }
        const llFilter = llBoost > 1.03
          ? `brightness(${llBoost.toFixed(2)}) contrast(1.06) saturate(1.1)`
          : "";
        // On first frame with real video dimensions, resize all canvases
        // to match the source aspect ratio. Portrait phone (720×1280)
        // no longer gets stretched to landscape 1920×1080.
        if (vid.videoWidth > 0 && vid.videoHeight > 0 &&
            (vid.videoWidth !== curWidth || vid.videoHeight !== curHeight)) {
          resizeAllCanvases(vid.videoWidth, vid.videoHeight);
        }

        // Iter208 — "none" mode means "no AI cutout, just show the raw
        // camera as-is". Short-circuit here so we don't segment or
        // paint anything else on top. We explicitly RESET all persistent
        // canvas state (filter, transform, compositeOp, alpha) before
        // drawing so a previous BLUR-mode frame's filter/transform
        // never leaks into the "none" render — this was the "OFF chip
        // still shows blur" bug from the user's screenshots.
        if (!bg || bg.mode === "none") {
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.filter = "none";
          ctx.globalCompositeOperation = "source-over";
          ctx.globalAlpha = 1.0;
          ctx.clearRect(0, 0, curWidth, curHeight);
          if (llFilter) ctx.filter = llFilter;
          ctx.drawImage(vid, 0, 0, curWidth, curHeight);
          ctx.filter = "none";
          rafId = requestAnimationFrame(tick);
          return;
        }
        // Reset canvas state each frame so mode-switches (e.g. from
        // BLUR → IMAGE) don't inherit a stale filter or transform.
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.filter = "none";
        ctx.globalCompositeOperation = "source-over";
        ctx.globalAlpha = 1.0;

        // 1) Paint background layer
        if (bg?.mode === "image" && bgImg.complete && bgImg.naturalWidth > 0) {
          drawCover(ctx, bgImg, curWidth, curHeight);
        } else if (bg?.mode === "video" && bgVid.readyState >= 2) {
          drawCover(ctx, bgVid, curWidth, curHeight);
        } else if (bg?.mode === "blur") {
          // Three intensity presets — light / medium / heavy. Legacy
          // "blur" (no level) defaults to medium so old prefs keep
          // working. Blur radius scales with intensity. Brightness dip
          // is subtle (0.95 / 0.85 / 0.75) — matches Zoom's default
          // vibe.
          const level = bg.blurLevel || "medium";
          const px = level === "light" ? 10 : level === "heavy" ? 40 : 20;
          const bright = level === "light" ? 0.95 : level === "heavy" ? 0.75 : 0.85;
          ctx.filter = `blur(${px}px) brightness(${bright})`;
          // Draw slightly larger than the canvas so the blurred edge
          // bleed doesn't show a hard rectangle inset.
          const pad = Math.max(10, px);
          ctx.drawImage(vid, -pad, -pad, curWidth + pad * 2, curHeight + pad * 2);
          ctx.filter = "none";
        } else if (bg?.mode === "dim") {
          // "Dim" — podcast-style darkened room, NO blur. Reads much
          // cleaner on camera than blur (no mush): the sharp subject
          // pops against the darkened scene, like stage lighting.
          ctx.filter = "brightness(0.4) saturate(0.75)";
          ctx.drawImage(vid, 0, 0, curWidth, curHeight);
          ctx.filter = "none";
        } else if (bg?.mode === "image" && !bgImg.complete) {
          // Image is still loading — show a subtle dark placeholder
          // instead of falling through to raw camera, so the user has
          // a clear visual cue that the AI cutout is "waiting on bg".
          ctx.fillStyle = "#0B1120";
          ctx.fillRect(0, 0, curWidth, curHeight);
        } else if (bg?.mode === "image") {
          // Image failed to load (CORS or 404) — dark placeholder + a
          // per-frame retry via setBgFromSettings above so a transient
          // network hiccup recovers automatically.
          ctx.fillStyle = "#111827";
          ctx.fillRect(0, 0, curWidth, curHeight);
        } else {
          // Should be unreachable now that "none" short-circuits above
          // — but keep a safe fallback of raw camera to avoid black
          // frames if a new bg mode is added later without handling.
          ctx.drawImage(vid, 0, 0, curWidth, curHeight);
        }

        // 2) Draw the camera — chroma-keyed when enabled, straight
        //    drawImage otherwise (lets the host see just the background
        //    change even when chroma is off — useful for blur).
        const wantPerson = ck && ck.mode === "person";
        if (wantPerson) {
          // Person Cutout · AI — Zoom-style smooth alpha mask.
          if (segBusy && performance.now() - segBusyAt > 1200) segBusy = false;
          ensureSegmenter().then((eng) => {
            if (disposed || !eng || segBusy) return;
            segBusy = true;
            segBusyAt = performance.now();
            try { eng.send(vid); }
            catch { segBusy = false; }
          });
          if (haveMask) {
            // iter208 fix — For BLUR mode specifically, we FIRST punch
            // the person's silhouette out of the blurred background
            // using the mask. Without this step, the feathered edges
            // of the sharp person (drawn on top) blend visually with
            // the BLURRED VERSION OF THE SAME PERSON underneath,
            // creating the "smoky hair ghost" the user saw in every
            // screenshot. Now the sharp person's feathered edges land
            // against the actual blurred ROOM behind them, which is
            // what Zoom / Meet do.
            if (bg?.mode === "blur" || bg?.mode === "dim") {
              ctx.globalCompositeOperation = "destination-out";
              ctx.filter = "none";
              ctx.drawImage(segMaskCanvas, 0, 0, curWidth, curHeight);
              ctx.globalCompositeOperation = "source-over";
            }

            // a) Draw camera onto subject canvas. Optional touch-up
            //    filter — soft Gaussian blur + brightness/contrast
            //    bump — mimics the "Touch Up My Appearance" toggle
            //    that Zoom / TikTok Live have. Off by default.
            subjectCtx.globalCompositeOperation = "source-over";
            const subjFilter = [
              tu ? "blur(1.5px) brightness(1.06) contrast(1.02) saturate(1.05)" : "",
              llFilter,
            ].join(" ").trim();
            subjectCtx.filter = subjFilter || "none";
            subjectCtx.clearRect(0, 0, curWidth, curHeight);
            subjectCtx.drawImage(vid, 0, 0, curWidth, curHeight);
            // b) Blurred-mask destination-in for feathered alpha.
            //    Feather is now RESOLUTION-RELATIVE (scaled against a
            //    720p-height reference) and tighter than before —
            //    default smoothness 0.15 ≈ 3 px @720p / 5 px @1080p
            //    (was a flat 6 px). Crisper "professional matte" edges;
            //    MediaPipe's internal temporal smoothing keeps them
            //    stable without extra blur.
            const sm = Math.max(0, Math.min(0.4, ck.smoothness ?? 0.15));
            const featherBase = (sm / 0.4) * 6 + 1; // 1..7 px @720p-equivalent
            const featherPx = Math.max(1, Math.round(featherBase * (curHeight / 1280)));
            subjectCtx.globalCompositeOperation = "destination-in";
            subjectCtx.filter = `blur(${featherPx}px)`;
            subjectCtx.drawImage(segMaskCanvas, 0, 0, curWidth, curHeight);
            subjectCtx.filter = "none";
            subjectCtx.globalCompositeOperation = "source-over";

            // Smart Frame — recompute the subject's bbox every 3rd frame
            // (~10 Hz) so the host can move around their room and stay
            // centered on viewers' screens. EMA smoothing keeps the
            // motion silky; brand-new mounts use a hard snap to avoid
            // animating from (0, 0, 1).
            if (sf) {
              if ((sfFrameSkip++ % 3) === 0) {
                sfCtx.clearRect(0, 0, SF_SAMPLE, SF_SAMPLE);
                sfCtx.drawImage(segMaskCanvas, 0, 0, SF_SAMPLE, SF_SAMPLE);
                const d = sfCtx.getImageData(0, 0, SF_SAMPLE, SF_SAMPLE).data;
                let minX = SF_SAMPLE, minY = SF_SAMPLE, maxX = 0, maxY = 0, hits = 0;
                for (let y = 0; y < SF_SAMPLE; y++) {
                  const row = y * SF_SAMPLE * 4;
                  for (let x = 0; x < SF_SAMPLE; x++) {
                    // MediaPipe writes mask with VARYING ALPHA — the
                    // alpha byte tells us subject confidence. We only
                    // count "confident person" pixels (alpha > 80) so
                    // background bleed doesn't pull the bbox outward.
                    if (d[row + x * 4 + 3] > 80) {
                      hits++;
                      if (x < minX) minX = x;
                      if (x > maxX) maxX = x;
                      if (y < minY) minY = y;
                      if (y > maxY) maxY = y;
                    }
                  }
                }
                // Need enough subject pixels to trust the bbox — under
                // ~1% coverage = subject probably out of frame, hold.
                if (hits > SF_SAMPLE * SF_SAMPLE * 0.01) {
                  const bw = (maxX - minX) / SF_SAMPLE;          // 0..1
                  const bh = (maxY - minY) / SF_SAMPLE;          // 0..1
                  const cx = ((minX + maxX) / 2) / SF_SAMPLE;    // 0..1
                  const cy = ((minY + maxY) / 2) / SF_SAMPLE;    // 0..1
                  // Talking-head detection — when the subject's bbox
                  // area is small AND the height/width ratio is in the
                  // "head + shoulders" band (1.0–1.7), the host is
                  // sitting / sat still doing podcast-style composition.
                  // We allow a tighter zoom (up to 2.5×) so the framing
                  // matches what creators do manually on TikTok / IG —
                  // bring me close. When the host stands or the bbox
                  // grows back, the EMA smoothly returns to wide.
                  const bboxArea = bw * bh;
                  const bboxAspect = bh / Math.max(bw, 0.001);
                  const isTalkingHead = bboxArea < 0.18 && bboxAspect > 1.0 && bboxAspect < 1.7;
                  // Iter204 fix — the previous values (TARGET_H=0.78,
                  // SCALE_MAX=2.5) produced a suffocating close-up that
                  // filled the frame with the subject's face and hid the
                  // background entirely (making the composite look
                  // broken). We now target the subject occupying ~60%
                  // of the canvas height and cap zoom at 1.6× so the
                  // background always stays visible around the person.
                  const TARGET_H = isTalkingHead ? 0.62 : 0.55;
                  const SCALE_MAX = isTalkingHead ? 1.6 : 1.35;
                  // Manual framing override — creators who want an
                  // exact vibe (TIGHT for podcast shoulders-up, WIDE
                  // for whole-room reveal) bypass the auto heuristic.
                  let scaleTarget;
                  if (fm === "wide") {
                    scaleTarget = 1.0;
                  } else if (fm === "tight") {
                    scaleTarget = Math.max(1.0, Math.min(2.0, 0.75 / Math.max(bh, 0.22)));
                  } else if (bh > 0.55) {
                    // Auto — skip when subject already fills the frame.
                    scaleTarget = 1.0;
                  } else {
                    scaleTarget = TARGET_H / Math.max(bh, 0.22);
                    scaleTarget = Math.max(1.0, Math.min(SCALE_MAX, scaleTarget));
                  }
                  // Translate so the scaled subject ends up centered on
                  // (width/2, height*0.58) — slightly above middle so
                  // shoulders + head sit in the ⅔ "rule" sweet spot.
                  const txTarget = (0.5 - cx * scaleTarget) * curWidth;
                  const tyTarget = (0.58 - cy * scaleTarget) * curHeight;
                  // EMA blend
                  if (sfTargetScale === 1 && sfTargetTx === 0 && sfTargetTy === 0) {
                    sfTargetScale = scaleTarget;
                    sfTargetTx = txTarget;
                    sfTargetTy = tyTarget;
                  } else {
                    sfTargetScale += (scaleTarget - sfTargetScale) * SF_EMA_ALPHA;
                    sfTargetTx += (txTarget - sfTargetTx) * SF_EMA_ALPHA;
                    sfTargetTy += (tyTarget - sfTargetTy) * SF_EMA_ALPHA;
                  }
                }
              }
              // Apply transform when compositing onto the program canvas.
              ctx.save();
              ctx.translate(sfTargetTx, sfTargetTy);
              ctx.scale(sfTargetScale, sfTargetScale);
              drawSubjectLayer(efx);
              ctx.restore();
            } else {
              // No Smart Frame — straight blit, also reset the EMA so
              // re-enabling later doesn't start from a stale framing.
              sfTargetTx = 0; sfTargetTy = 0; sfTargetScale = 1; sfFrameSkip = 0;
              drawSubjectLayer(efx);
            }
          }
          // No mask yet — let background show through (first ~200ms).
        } else if (ck && ck.enabled !== false && typeof ck.color === "string") {
          drawChromaKeyed(ctx, vid, curWidth, curHeight, ck);
        } else {
          ctx.drawImage(vid, 0, 0, curWidth, curHeight);
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    // ---- Boot: wait for real camera dimensions, THEN capture ----
    // Portrait phones report 720×1280 within a few frames. We size all
    // canvases to the source's real aspect BEFORE calling
    // captureStream(), because WebKit kills capture streams whose
    // source canvas is resized afterwards. Falls back to the requested
    // dims after 4s so a stalled camera can't block publish forever.
    let bootTimer = 0;
    const bootT0 = performance.now();
    const boot = () => {
      if (disposed) return;
      const vw = vid.videoWidth || 0;
      const vh = vid.videoHeight || 0;
      if (!(vw > 0 && vh > 0) && performance.now() - bootT0 < 4000) {
        bootTimer = setTimeout(boot, 60);
        return;
      }
      const w = vw > 0 ? vw : width;
      const h = vh > 0 ? vh : height;
      curWidth = w;
      curHeight = h;
      canvas.width = w; canvas.height = h;
      segMaskCanvas.width = w; segMaskCanvas.height = h;
      subjectCanvas.width = w; subjectCanvas.height = h;
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, w, h);
      produced = canvas.captureStream(captureFps);
      producedTrack = produced.getVideoTracks()[0];
      // "detail" tells the encoder to preserve RESOLUTION over framerate
      // — right trade for a composited talking-head feed (was "motion",
      // which let the encoder downscale under pressure → mushy image).
      try { if (producedTrack) producedTrack.contentHint = "detail"; } catch {}
      setTrack(producedTrack);
      if (dbg) console.log("[useGreenScreen] pipeline built, track published", { trackId: producedTrack?.id, w, h });
      tick();
    };
    boot();

    return () => {
      disposed = true;
      try { clearTimeout(bootTimer); } catch {}
      try { cancelAnimationFrame(rafId); } catch {}
      try { produced?.getTracks().forEach((t) => t.stop()); } catch {}
      try { vid.srcObject = null; } catch {}
      try { hiddenWrap.remove(); } catch {}
      try { engine?.close?.(); } catch {}
      engine = null;
      haveMask = false;
      subjectCanvasRef.current = null;
      videoElRef.current = null;
      setTrack(null);
      setBgLoading(false);
      setBgFailed(false);
      setReady(false);
      setSegStatus("idle");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream, enabled, width, height, captureFps]);

  return { track, bgLoading, bgFailed, ready, segStatus, subjectCanvasRef, videoElRef };
}

/** Chroma-key `vid` onto `ctx` in-place. */
function drawChromaKeyed(ctx, vid, w, h, chroma) {
  // Fast path — when chroma is "enabled:false" we draw opaque.
  if (!chroma || chroma.enabled === false) {
    ctx.drawImage(vid, 0, 0, w, h);
    return;
  }
  const off = document.createElement("canvas");
  off.width = w;
  off.height = h;
  const octx = off.getContext("2d", { willReadFrequently: true });
  octx.drawImage(vid, 0, 0, w, h);
  const frame = octx.getImageData(0, 0, w, h);
  const data = frame.data;
  const key = hexToRgb(chroma.color || "#00FF00");
  const sim = (chroma.similarity ?? 0.4) * 255;
  const smoothPx = Math.max(1, sim * ((chroma.smoothness ?? 0.1) * 2));
  for (let i = 0; i < data.length; i += 4) {
    const dr = data[i] - key.r;
    const dg = data[i + 1] - key.g;
    const db = data[i + 2] - key.b;
    const dist = Math.sqrt(dr * dr + dg * dg + db * db);
    if (dist < sim) {
      data[i + 3] = 0;
    } else if (dist < sim + smoothPx) {
      data[i + 3] = Math.round(((dist - sim) / smoothPx) * 255);
    }
  }
  octx.putImageData(frame, 0, 0);
  ctx.drawImage(off, 0, 0);
}
